import React, { useState, useEffect, useContext, useRef } from 'react'

//Node Modules
import { 
  useParams, 
  useLocation
} from 'react-router-dom'
import axios from 'axios'
import { changeLanguage, resolveLocale } from 'i18n'
import ReactGA from 'react-ga'
import ReactGA4 from 'react-ga4'
import i18n from 'i18next'

//Components
import CDC from 'components/CDC'
import Collection from 'components/Collection'
import Docset from 'components/Docset'
import LocaleAvailableModal from 'components/LocaleAvailableModal'
import RedirectLayout from 'components/RedirectLayout'


//Context
import { LanguageContext } from 'context/language-context'
import { LoadingContext } from 'context/loading-context'
import { MessagesProvider } from 'context/messages-context'
import { CasFormatProvider, CasLanguageProvider } from 'context/cas-format-context'
import { SecureContext, ToggleSecureContext, CurrentContentContext, ToggleUnavailableContext, ParentSortContext, NodeInfoContext } from 'context/content-context'
import { HashContext, PageContext } from 'context/page-context'
import { TitleState } from 'context/title-context'
import { DocsetLatestContext } from 'context/docset-latest-context'
import { RequestorContext } from 'context/content-context'

// API
import { trackCDCNavigation, trackDocsetNavigation } from 'api/tracking'
import { getLatestCFR } from 'api/docset'


// Util
import { INIT_LOCALE_MODAL_DISMISSED, DOCSET_LATEST_CFR } from 'util/constants'

// Hooks
import { useSessionStorage } from 'hooks'

interface RouteParams {
  lang: string
  collection?: string
  cdc?: string
  docset: string
  cfr: string
  page: string
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ContentPage = () => {
  const title = useContext(TitleState)
  const prevTitle = useRef(title)
  const {
    lang,
    collection,
    cdc,
    docset,
    cfr,
    page
  } = useParams<RouteParams>()
  const location = useLocation()
  const [initLocaleModalDismissed, setInitLocaleModalDismissed] = useSessionStorage<boolean>(INIT_LOCALE_MODAL_DISMISSED, false)
  axios.defaults.headers.common['Accept-Language'] = lang
  const [latestCFR, setLatestCFR] = useState<string | undefined>(undefined)
  const [latestRetrieved, setLatestRetrieved] = useState<Boolean>(false)

  const hash = window.location.hash

  useEffect(()=>{
      getLatestCFR(docset, lang).then( resp => {
        setLatestCFR(resp as string)
        setLatestRetrieved(true)
      }, err=>{
        setLatestRetrieved(true)
      })
  },[lang, docset])

  useEffect(()=>{
    //check to make sure lang is set correctly
    if (i18n.language !== resolveLocale(lang)) {
      i18n.changeLanguage(resolveLocale(lang))
    }
  },[lang, cfr])
  
  useEffect(() => {
    changeLanguage(lang)    
  }, [lang])

  useEffect(() => {

    // Only track changes once the title has changed
    if (!title || title === prevTitle.current) {
      return
    }

    const referrer = document.referrer

    prevTitle.current = title

    if (cdc) {
      trackCDCNavigation(cdc, cfr, docset, page, hash, referrer, title)
    } else {
      trackDocsetNavigation(docset, cfr, page, hash, referrer, title)
    }
  }, [lang, cdc, docset, cfr, page, hash, title])
  
  React.useEffect(() => {
    const url = `${location.pathname}${location.search}`

    ReactGA.pageview(url)
    ReactGA4.send(url)
  }, [location])

  const help = useQuery().has('help')
  const homeOnFail = useQuery().has('homeOnFail')
  const fromDefault = useQuery().has('fromDefault')
  const requestorId = useQuery().get('requestorId')

  const initShowLocaleModal = fromDefault && lang !== 'en'

  const [secure, setSecure] = useState(false)  
  const setSecureContext = (isSecure: boolean) => {
    setSecure(isSecure)
  }

  const [parentSort, setParentSort] = useState(false)
  const setParentSortContext = (isParentSort: boolean) => {
    setParentSort(isParentSort)
  }

  const [nodeInfo, setNodeInfo] = useState({embeddedNode: false, parentTitle: '', parentPage: '', parentDocset: ''})
  const setNodeInfoContext = (embeddedNode: boolean, parentTitle: string, parentPage: string, parentDocset: string) => {
    setNodeInfo({embeddedNode: embeddedNode, parentDocset: parentDocset, parentTitle: parentTitle, parentPage: parentPage})
  }

  const [secureToggled, setSecureToggled] = useState(false)

  const [unavailableToggled, setUnavailableToggled] = useState<string>('')

  const [loading, setLoading] = useState(true)
  const setLoadingContext = (isLoading: boolean) => {
    setLoading(isLoading)
  }

  const [currentContent, setCurrentContent] = useState({alias: '', cfr: '', page: ''})
  const setCurrentContentContext = (alias: string, cfr: string, page:string) => {
    setCurrentContent({alias:alias, cfr: cfr, page:page})
  }
  
  const [showLocaleModal, setShowLocaleModal] = useState(initShowLocaleModal)
  
  let content = <></>

  const dismissLocaleModal = () => {
    setShowLocaleModal(false)
    setInitLocaleModalDismissed(true)
  }

  const initLocaleModal = <LocaleAvailableModal onDismiss={dismissLocaleModal} />
  if ( page.toLowerCase().indexOf('.pdf') !== -1 ) { //check to see if pdf link 
    content = <><RedirectLayout lang={lang} collection={collection} cdc={cdc} docset={docset} cfr={cfr} page={page}/></>
  } else if (collection && cdc) {
    content = <Collection.Container lang={lang} alias={collection} cfr={cfr} cdcAlias={cdc} docsetAlias={docset} page={page} help={help} homeOnFail={homeOnFail} />
  } else if (cdc) {
    content = <CDC.Container lang={lang} alias={cdc} cfr={cfr} docsetAlias={docset} page={page} help={help} homeOnFail={homeOnFail} />
  } else {
    content = latestRetrieved ?
      <Docset.Container 
        lang={lang} 
        alias={docset} 
        cfr={latestCFR && cfr === DOCSET_LATEST_CFR ? latestCFR : cfr} 
        page={page} 
        help={help} 
        homeOnFail={homeOnFail} 
      /> : <div></div>
  }

  function toggleSecure() {
    setSecureToggled(!secureToggled)
  }

  function toggleUnavailable(flag: string) {
    setUnavailableToggled(flag)
  }

  useEffect(()=>{
    if (homeOnFail) {
      let url = window.location.href.replace("?homeOnFail","")
      window.history.replaceState(null, '', url)
      
    }
  },[]) /* eslint-disable-line */

  return (
    <LanguageContext.Provider value={lang}>
      {/* <TitleProvider> */}
      <MessagesProvider>
        <SecureContext.Provider value={{secure, setSecureContext}}>
          <NodeInfoContext.Provider value = {{nodeInfo, setNodeInfoContext}}>
            <ParentSortContext.Provider value={{parentSort, setParentSortContext}}>
              <ToggleSecureContext.Provider value={{secureToggled, toggleSecure}}>
                <ToggleUnavailableContext.Provider value = {{unavailableToggled, toggleUnavailable}}>
                  <LoadingContext.Provider value={{loading, setLoadingContext}}>
                    <CasFormatProvider>
                      <CasLanguageProvider>
                        <PageContext.Provider value={page}>
                          <HashContext.Provider value={hash}>
                            <CurrentContentContext.Provider value={{currentContent, setCurrentContentContext}}>
                              <DocsetLatestContext.Provider value={latestCFR}>
                                <RequestorContext.Provider value={requestorId}>
                                  { !initLocaleModalDismissed && showLocaleModal && initLocaleModal }
                                  { content }
                                </RequestorContext.Provider>
                              </DocsetLatestContext.Provider>
                            </CurrentContentContext.Provider>
                          </HashContext.Provider>
                        </PageContext.Provider>
                      </CasLanguageProvider>
                    </CasFormatProvider>
                  </LoadingContext.Provider>
                </ToggleUnavailableContext.Provider >
              </ToggleSecureContext.Provider>
            </ParentSortContext.Provider>
          </NodeInfoContext.Provider>
        </SecureContext.Provider>
      </MessagesProvider>
      {/* </TitleProvider> */}
    </LanguageContext.Provider>
  )
}

export default ContentPage